import { View } from 'react-native';
import Animated from 'react-native-reanimated';
import { twJoin } from 'tailwind-merge';

import { useExtractedQuestionsBubbleContext } from '@/components/chat/bubbles/extractedQuestions/ExtractedQuestionsBubbleContext';
import {
  QUESTION_PILL_VIEW_STYLE,
  getQuestionPillColors,
} from '@/components/chat/bubbles/extractedQuestions/questionPillStyle';
import { HStack } from '@/components/ui/hstack';
import { Text } from '@/components/ui/text';
import { useQuestionPulseStyle } from '@/hooks/chat/useQuestionPulseStyle';
import { useColors } from '@/hooks/useColors';
import type { ExtractedQuestion } from '@/types/extractedQuestions';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { getAccessibleHitSlop } from '@/util/accessibleTouchTarget';
import { getPressProps } from '@/util/pressProps';

interface QuestionCardProps {
  question: ExtractedQuestion;
  questionIndex: number;
  isPrimary: boolean;
  position: 'left' | 'right';
  /** The recommended layout: upright text under a hanging "Q1" marker that pulses its chip. */
  hanging?: boolean;
}

/**
 * One question card: small ordinal pill (`Q{n}`) on the left and
 * the normalized `directQuestion` text in italics on the right.
 *
 * Position-aware: pill matches the inline chip color so chip ↔ card linking
 * is visually obvious. Pulses its container background when the matching
 * inline chip or highlight is tapped.
 */
export function QuestionCard({
  question,
  questionIndex,
  isPrimary,
  position,
  hanging = false,
}: QuestionCardProps) {
  const { colors } = useColors();

  const { pulseQuestion } = useExtractedQuestionsBubbleContext();

  const isRight = position === 'right';

  const pulseColor = isRight && !hanging ? colors.primary[700] : colors.primary[100];

  const animatedStyle = useQuestionPulseStyle(questionIndex, {
    baseColor: 'transparent',
    pulseColor,
  });

  const { backgroundColor: pillBackgroundColor, textColor: pillTextColor } = getQuestionPillColors(
    colors,
    position,
    hanging
  );

  const bodyColorClassName = isRight ? 'text-typography-white' : 'text-typography-700';

  if (hanging) {
    return (
      <Animated.View
        style={animatedStyle}
        {...getAccessibilityProps({
          role: 'article',
          label: `Question ${questionIndex + 1}: ${question.directQuestion}`,
        })}>
        <HStack className="items-start">
          <Text
            className={twJoin(
              'w-[30px] font-semibold text-[11px] leading-5 web:cursor-pointer',
              isRight ? 'text-typography-white' : 'text-typography-700'
            )}
            {...getPressProps(() => pulseQuestion(questionIndex), getAccessibleHitSlop(24))}
            {...getAccessibilityProps({
              label: `Question ${questionIndex + 1}: highlight in message`,
              role: 'button',
            })}>
            {`Q${questionIndex + 1}`}
          </Text>
          <Text size="sm" className="flex-1 leading-5 text-typography-800">
            {question.directQuestion}
          </Text>
        </HStack>
      </Animated.View>
    );
  }

  return (
    <Animated.View
      style={[animatedStyle, CARD_BASE_STYLE]}
      {...getAccessibilityProps({
        role: 'article',
        label: `Question ${questionIndex + 1}: ${question.directQuestion}`,
      })}>
      <HStack space="md" className="items-start">
        <View
          style={[
            QUESTION_PILL_VIEW_STYLE,
            PILL_LAYOUT_STYLE,
            { backgroundColor: pillBackgroundColor },
          ]}>
          <Text size="2xs" className="font-bold" style={{ color: pillTextColor }}>
            {`Q${questionIndex + 1}`}
          </Text>
        </View>
        <Text
          size="sm"
          className={twJoin(
            'flex-1 italic leading-snug',
            bodyColorClassName,
            isPrimary ? 'font-medium' : ''
          )}>
          {question.directQuestion}
        </Text>
      </HStack>
    </Animated.View>
  );
}

/** Layout offset to vertically align the pill with the body text within the
 * `items-start` HStack; the visual style itself is inherited from the inline
 * chip via `QUESTION_PILL_VIEW_STYLE`. */
const PILL_LAYOUT_STYLE = {
  marginTop: 2,
};

const CARD_BASE_STYLE = {
  borderRadius: 8,
  paddingHorizontal: 8,
  paddingVertical: 4,
};
